import React from "react";
import { useNavigate } from "react-router-dom";
import {
  LandingActionArrowIcon,
  LandingDiscoverExperienceIcon,
  LandingPostExperienceIcon,
  LandingVerticalExperienceIcon,
} from "../../assets/icons/LandingExperienceIcons";
import {
  FEED_TEMPLATE_TYPE_STORAGE_KEY,
  SDK_TEMPLATE_TYPE_STORAGE_KEY,
} from "../../app/constants/template";
import { TemplateType } from "../../app/types/template";

type FeedTemplateType = "POST" | "VERTICAL_VIDEO";
type LandingIconType = "vertical" | "post" | "discover";

const renderLandingCardIcon = (icon: LandingIconType) => {
  if (icon === "vertical") {
    return <LandingVerticalExperienceIcon />;
  }

  if (icon === "post") {
    return <LandingPostExperienceIcon />;
  }

  return <LandingDiscoverExperienceIcon />;
};

const LANDING_COLUMNS = [
  {
    id: "vertical-video",
    title: "Vertical Feed",
    tag: "Immersive",
    descriptionLines: [
      "Scroll through your personalized content stream.",
      "Discover stories, updates, and moments that matter to you.",
    ],
    actionLabel: "Explore Feed",
    route: "/feed",
    templateType: "VERTICAL_VIDEO" as FeedTemplateType,
    sdkTemplateType: "VERTICAL_VIDEO" as TemplateType,
    accentClass: "landing-card--vertical",
    icon: "vertical" as LandingIconType,
  },

  {
    id: "post",
    title: "News Feed",
    tag: "Curated",
    descriptionLines: [
      "Stay informed with the latest headlines and breaking news.",
      "Curated articles from trusted sources, tailored to your interests.",
    ],
    actionLabel: "Read News",
    route: "/feed",
    templateType: "POST" as FeedTemplateType,
    sdkTemplateType: "POST" as TemplateType,
    accentClass: "landing-card--post",
    icon: "post" as LandingIconType,
  },

  {
    id: "discover",
    title: "Discover as home",
    tag: "Exploratory",
    descriptionLines: [
      "Explore new worlds, places, and experiences waiting for you.",
      "Venture beyond the familiar and find inspiration everywhere.",
    ],
    actionLabel: "Start Exploring",
    route: "/discover",
    sdkTemplateType: "DISCOVER" as TemplateType,
    accentClass: "landing-card--discover",
    icon: "discover" as LandingIconType,
  },
];

export default function Landing() {
  const navigate = useNavigate();

  const handleColumnClick = (item: (typeof LANDING_COLUMNS)[number]) => {
    localStorage.setItem(SDK_TEMPLATE_TYPE_STORAGE_KEY, item.sdkTemplateType);

    if (item.templateType) {
      localStorage.setItem(FEED_TEMPLATE_TYPE_STORAGE_KEY, item.templateType);
      navigate(item.route, {
        state: {
          templateType: item.templateType,
          sdkTemplateType: item.sdkTemplateType,
        },
      });
      return;
    }

    navigate(item.route, {
      state: {
        sdkTemplateType: item.sdkTemplateType,
      },
    });
  };

  return (
    <main className="landing-page" aria-label="Landing page">
      <section className="landing-hero">
        <p className="landing-hero__badge">Personalized For You</p>
        <h1 className="landing-hero__title">Choose your experience</h1>
        <p className="landing-hero__description">
          Each view is crafted for a different way you like to consume content.
          Pick what fits your mood.
        </p>
      </section>

      <section className="landing-cards" aria-label="Content type navigation">
        {LANDING_COLUMNS.map((item) => (
          <div className="landing-card__container" key={item.id}>
            <button
              type="button"
              className={`landing-card ${item.accentClass}`}
              onClick={() => handleColumnClick(item)}
            >
              <span className="landing-card__icon" aria-hidden="true">
                {renderLandingCardIcon(item.icon)}
              </span>
              <p className="landing-card__tag">{item.tag}</p>
              <h2 className="landing-card__title">{item.title}</h2>
              {item.descriptionLines.map((line) => (
                <p key={line} className="landing-card__copy">
                  {line}
                </p>
              ))}
              <div className="landing-card__action">
                <span className="landing-card__action-text">
                  {item.actionLabel}
                </span>
                <span className="landing-card__action-arrow">
                  <LandingActionArrowIcon />
                </span>
              </div>
            </button>
            <span className="landing-card__hover-bg"></span>
          </div>
        ))}
      </section>
    </main>
  );
}
